import { Button, Checkbox, Form, Input, Select } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "../../../hooks/useTranslation";
import dictionaryStorage from "../../../storages/dictionary.storage";
import partTypesStorage from "../../../storages/part-types";
import templatesStorage from "../../../storages/templatesStorage";
import Toolbar from "../../../v2/shared-components/Toolbar";
import { DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined } from '@ant-design/icons';

export interface TemplateEditFormProps {
  onSave?: () => void;
}

// Content component that uses the templates namespace
function TemplateEditForm({ onSave }: TemplateEditFormProps) {
  const { t } = useTranslation('templates');
  const {
    selectedTemplate,
    addPartGroup,
    changePartGroup,
    removePartGroup,
    movePartGroupUp,
    movePartGroupDown,
    saveTemplate,
  } = templatesStorage;

  useEffect(() => {
    partTypesStorage.loadPartTypes();
    templatesStorage.loadPartsDictionary();
  }, []);

  if (!selectedTemplate) {
    return (
      <div className="template-edit-form-empty">
        <p>{t('editForm.placeholder')}</p>
      </div>
    );
  }

  const handleSave = () => {
    saveTemplate();
    if (onSave) {
      onSave();
    }
  };

  return (
    <div className="template-edit-form" style={{ width: 'calc(100% - 8px)' }}>
      <div 
        className="template-edit-form-toolbar" 
        style={{
          position: 'sticky',
          top: '-32px',
          zIndex: 10,
          backgroundColor: 'white',
          padding: '10px 0',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)', padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
          <h2 style={{ margin: 0 }}>{selectedTemplate._id ? t('editForm.title.edit', { title: selectedTemplate.title }) : t('editForm.title.create')}</h2>
          <Toolbar>
            <Button
              type="primary"
              onClick={handleSave}
            >
              {selectedTemplate._id ? t('editForm.button.save') : t('editForm.button.add')}
            </Button>
            <Button onClick={() => addPartGroup()}>{t('editForm.button.addPartGroup')}</Button>
          </Toolbar>
        </div>
      </div>
      <div>
      <Form.Item
          label={t('editForm.field.title.label')}
          name="title"
          rules={[{ required: true, message: t('editForm.field.title.required') }]}
          style={{ width: '100%' }}
        >
          <Input
            placeholder={t('editForm.field.title.placeholder')}
            defaultValue={selectedTemplate.title}
            onChange={(e) => {
              templatesStorage.changeTemplateField("title", e.target.value);
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('editForm.field.itemType.label')}
          name="itemType"
          rules={[{ required: true, message: t('editForm.field.itemType.required') }]}
          style={{ width: '100%' }}
        >
          <Select
            defaultValue={selectedTemplate.type}
            showSearch
            onChange={(value) => {
              templatesStorage.changeTemplateField("type", value);
            }}
            options={['droneRtu', 'batteryRtu'].map((group) => ({
              label: group,
              value: group,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={t('editForm.field.description.label')}
          name="description"
          rules={[{ required: true, message: t('editForm.field.description.required') }]}
        >
          <Input.TextArea
            placeholder={t('editForm.field.description.placeholder')}
            defaultValue={selectedTemplate.description}
            onChange={(e) => {
              templatesStorage.changeTemplateField("description", e.target.value);
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('editForm.field.total.label')}
          name="total"
          rules={[{ required: true, message: t('editForm.field.total.required') }]}
        >
          <Input
            placeholder={t('editForm.field.total.placeholder')}
            defaultValue={selectedTemplate.total}
            onChange={(e) => {
              templatesStorage.changeTemplateField("total", e.target.value);
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('editForm.field.currency.label')}
          name="currency"
          rules={[{ required: true, message: t('editForm.field.currency.required') }]}
        >
          <Select
            defaultValue={selectedTemplate.currency}
            showSearch
            onChange={(value) => {
              templatesStorage.changeTemplateField("currency", value);
            }}
            options={['USD'].map((currency) => ({
              label: currency,
              value: currency,
            }))}
          />
        </Form.Item>
      </div>
      {selectedTemplate.possibleParts.map((possiblePart, indexPart: number) => {
        const mainPartTypes = partTypesStorage.mainPartTypeNames;
        return (
          <div 
            key={`partGroup-${possiblePart.groupId}-${indexPart}`} 
            style={{ 
              width: '100%', 
              marginRight: '8px',
              padding: '8px', 
              paddingRight: '32px',
              position: 'relative',
              border: '1px solid #f0f0f0', 
              borderRadius: '4px' 
            }}
          >
            <Toolbar 
              style={{ 
                position: 'absolute', 
                top: '50%', 
                right: '-24px',
                // right: '12px', 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
                backgroundColor: 'white',
                boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
                border: '1px solid #f0f0f0',
              }}
            >
              {indexPart > 0 && <Button 
                icon={<ArrowUpOutlined />} 
                onClick={() => movePartGroupUp(indexPart)} 
                size="small"
                title={t('editForm.button.moveUp')}
              />}
              {indexPart < selectedTemplate.possibleParts.length - 1 && <Button 
                icon={<ArrowDownOutlined />} 
                onClick={() => movePartGroupDown(indexPart)} 
                size="small"
                title={t('editForm.button.moveDown')}
              />}
              <Button 
                icon={<DeleteOutlined />} 
                danger 
                onClick={() => removePartGroup(indexPart)} 
                size="small"
                title={t('editForm.button.delete')}
              />
            </Toolbar>
            <Form layout="horizontal" style={{ width: '100%', paddingTop: '16px' }}>
              <Form.Item
                label={t('editForm.partGroup.label')}
                name="partGroup"
                rules={[{ required: true, message: t('editForm.partGroup.required') }]}
                style={{ width: '100%' }}
              >
                <Select
                  defaultValue={possiblePart.groupId}
                  showSearch
                  onChange={(value) => {
                    const newPartGroup = {
                      ...possiblePart,
                      groupId: value,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                  options={mainPartTypes?.map((ptName) => {
                    const currentPt = dictionaryStorage.productGroups?.find((group) => ptName === group.groupId);
                    return ({
                      label: currentPt?.title?.uk || ptName,
                      value: ptName,
                    });
                  })}
                />
              </Form.Item>
              <Form.Item
                label={t('editForm.partGroup.title.label')}
                name="title"
                rules={[{ required: true, message: t('editForm.partGroup.title.required') }]}
              >
                <Input
                  defaultValue={possiblePart.title}
                  onChange={(e) => {
                    const newPartGroup = {
                      ...possiblePart,
                      title: e.target.value,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('editForm.partGroup.parts.label')}
                name="parts"
                rules={[{ required: true, message: t('editForm.partGroup.parts.required') }]}
                style={{ width: '100%' }}
              >
                <Select
                  defaultValue={possiblePart.partNames}
                  mode="tags"
                  style={{ width: '100%' }}
                  showSearch
                  onChange={(value) => {
                    const newPartGroup = {
                      ...possiblePart,
                      partNames: value,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                  options={templatesStorage
                    .partsDictionary
                    .filter((partType) => {
                      return partType.partType === possiblePart.groupId;
                    })
                    .map((partType) => partType.shortName)
                    .flat()
                    .map((partTypeName) => ({
                      label: partTypeName,
                      value: partTypeName,
                    }))}
                />
              </Form.Item>
              <Form.Item
                label={t('editForm.partGroup.targetPrice.label')}
                name="targetPrice"
                rules={[{ required: true, message: t('editForm.partGroup.targetPrice.required') }]}
                style={{ width: '100%' }}
              >
                <Input
                  defaultValue={possiblePart.targetPrice}
                  onChange={(e) => {
                    let targetPrice = parseFloat(e.target.value);
                    if (isNaN(targetPrice)) {
                      targetPrice = possiblePart.targetPrice || 0;
                    }
                    const newPartGroup = {
                      ...possiblePart,
                      targetPrice,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('editForm.partGroup.quantity.label')}
                name="quantity"
                rules={[{ required: true, message: t('editForm.partGroup.quantity.required') }]}
                style={{ width: '100%' }}
              >
                <Input
                  defaultValue={possiblePart.defaultQuantity}
                  onChange={(e) => {
                    let quantity = parseFloat(e.target.value);
                    if (isNaN(quantity)) {
                      quantity = possiblePart.defaultQuantity || 0;
                    }
                    const newPartGroup = {
                      ...possiblePart,
                      defaultQuantity: quantity,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('editForm.partGroup.needSetUpQuantity.label')}
                name="needSetUpQuantity"
                style={{ width: '100%' }}
              >
                <Checkbox
                  defaultChecked={possiblePart.isMultiUnit}
                  onChange={(e) => {
                    const newPartGroup = {
                      ...possiblePart,
                      isMultiUnit: e.target.checked,
                    }
                    changePartGroup(indexPart, newPartGroup);
                  }}
                />
              </Form.Item>
            </Form>
          </div>
        )
      })}
      
      <div style={{ width: '100%', marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
        <Button 
          onClick={() => addPartGroup()} 
          type="dashed" 
          style={{ width: '100%', height: '48px' }}
          icon={<PlusOutlined />}
        >
          Add part group
        </Button>
      </div>
    </div>
  );
}

export default observer(TemplateEditForm);
